/**
 * Augmentation schedule utilities
 * Determines when mid-game augmentation selection rounds are triggered
 */
import type { Color } from "../types/common";
import type { ChessState } from "../types/game-state";

/** Number of augmentation choices offered to each player per round */
export const AUGMENTATION_CHOICES_PER_ROUND = 3;

/**
 * Maps augmentationRound → turnNumber at which the next selection is triggered.
 * Round 1 completed (augmentationRound=1) triggers at turnNumber=6
 * (i.e., after black's 5th move, which increments turn from 5 to 6).
 */
export const AUGMENTATION_TRIGGER_TURNS: Record<number, number> = {
  1: 6,
};

export interface AugmentationScheduleInfo {
  scheduledTurnNumber: number;
  turnsUntil: number;
}

/**
 * Returns which player should select their augmentation next, or null if
 * not in the selection phase or both players have already selected.
 * White selects first, then black.
 */
export function getSelectingPlayer(state: ChessState): Color | null {
  if (
    state.phase !== "AUGMENTATION_SELECTION" ||
    !state.augmentationSelection
  ) {
    return null;
  }
  if (!state.augmentationSelection.whiteSelected) return "WHITE";
  if (!state.augmentationSelection.blackSelected) return "BLACK";
  return null;
}

/**
 * Returns the available augmentation choice IDs for the given player,
 * or an empty array if the state is not in the selection phase.
 */
export function getAugmentationChoiceIds(
  state: ChessState,
  player: Color,
): string[] {
  if (!state.augmentationSelection) return [];
  return player === "WHITE"
    ? (state.augmentationSelection.whiteChoices ?? [])
    : (state.augmentationSelection.blackChoices ?? []);
}

/**
 * Returns the next augmentation selection schedule info, or null if:
 * - Already in AUGMENTATION_SELECTION phase
 * - No more scheduled rounds for the current augmentationRound
 * - The trigger turn has already passed
 */
export function getAugmentationScheduleInfo(
  state: ChessState,
): AugmentationScheduleInfo | null {
  if (state.phase === "AUGMENTATION_SELECTION") return null;

  const triggerTurn = AUGMENTATION_TRIGGER_TURNS[state.augmentationRound];
  if (triggerTurn === undefined) return null;

  const turnsUntil = triggerTurn - state.turnNumber;
  if (turnsUntil <= 0) return null;

  return { scheduledTurnNumber: triggerTurn, turnsUntil };
}
